"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useAtom } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { AssistantsService, ConversationsService } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, prefetchQuery } from "@/utils/useRemoteData";
import { genieConversationByTenantAtom } from "@/store/genie-atoms";

export function useGenieConversation(isOpen: boolean) {
  const { spotGroup, spotUser } = useSpotUser();
  const tenantId = spotUser.tenant_id;
  const [byTenant, setByTenant] = useAtom(genieConversationByTenantAtom);
  const conversationId = byTenant[tenantId];
  const [assistantId, setAssistantId] = useState("");
  const queryClient = useQueryClient();
  const creatingRef = useRef(false);

  const setConversationId = useCallback(
    (id: string | undefined) => {
      setByTenant((prev) => {
        if (!id) {
          if (!(tenantId in prev)) return prev;
          const next = { ...prev };
          delete next[tenantId];
          return next;
        }
        if (prev[tenantId] === id) return prev;
        return { ...prev, [tenantId]: id };
      });
    },
    [setByTenant, tenantId]
  );

  const ensureAssistant = useCallback(async (): Promise<string | undefined> => {
    if (assistantId) return assistantId;
    try {
      const genieAssistant = await AssistantsService.getOrCreateGenieAssistant({
        path: { group_id: spotGroup.id },
      });
      setAssistantId(genieAssistant.data.id);
      return genieAssistant.data.id;
    } catch {
      return undefined;
    }
  }, [assistantId, spotGroup.id]);

  const createConversation = useCallback(
    async (force = false): Promise<string | undefined> => {
      if ((!force && conversationId) || creatingRef.current) {
        return conversationId;
      }
      creatingRef.current = true;
      try {
        const aid = await ensureAssistant();
        if (!aid) throw new Error("no genie assistant");
        const conversation = await ConversationsService.createConversation({
          path: { group_id: spotGroup.id },
          query: { assistant_id: aid },
          body: { name: "Genie" },
        });
        const id = conversation.data.id;
        await prefetchQuery(queryClient, DATA_KEYS.CONVERSATION, {
          path: { group_id: spotGroup.id, conversation_id: id },
          query: { paged: true },
        });
        setConversationId(id);
        return id;
      } catch {
        SpotToast.error("Failed to start Genie session");
        return undefined;
      } finally {
        creatingRef.current = false;
      }
    },
    [
      conversationId,
      setConversationId,
      ensureAssistant,
      queryClient,
      spotGroup.id,
    ]
  );

  useEffect(() => {
    if (isOpen && !assistantId) {
      ensureAssistant();
    }
  }, [isOpen, assistantId, ensureAssistant]);

  // Drop a restored conversation that no longer exists. No "active" flag: dev
  // StrictMode's double-invoke would cancel the only fetch and skip the drop.
  const validatedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isOpen || !conversationId) return;
    if (validatedRef.current === conversationId) return;
    const id = conversationId;
    validatedRef.current = id;
    // Paged: only the 404 matters here, and a long chat is megabytes in full.
    ConversationsService.getConversation({
      path: { group_id: spotGroup.id, conversation_id: id },
      query: { paged: true },
    }).catch((error) => {
      // Only a 404 means the conversation is gone; a transient failure must
      // not wipe the tenant-wide pointer.
      if (!(error instanceof AxiosError) || error.response?.status !== 404) {
        validatedRef.current = undefined;
        return;
      }
      setByTenant((prev) => {
        if (prev[tenantId] !== id) return prev;
        const next = { ...prev };
        delete next[tenantId];
        return next;
      });
    });
  }, [isOpen, conversationId, spotGroup.id, setByTenant, tenantId]);

  useEffect(() => {
    if (isOpen && !conversationId) {
      createConversation();
    }
  }, [isOpen, conversationId, createConversation]);

  return { conversationId, setConversationId, createConversation, assistantId };
}
